// stores/cart.ts
import { defineStore } from 'pinia'
import { $fetch } from 'ofetch'
import { getWidgetConfig } from '../runtime/widgetConfig'
import { useProjectStore } from './useProjectStore'
import { useImageDB, type ImageRecord } from '../utils/imageDb'
import { resolveFrozenOrientation } from '../utils/persistedOrientation'

const CART_TOKEN_KEY = 'cart_token'

// ---- Cross-widget live sync (no infinite loops) -------------------------
// We emit "cart changed" ONLY for mutations (add/remove/qty), NOT for fetchCart().
// Otherwise a widget that refreshes on "cart changed" would cause a refresh loop.
const CART_SYNC_LS_KEY = 'pfe_cart_sync_v1'
const CART_SYNC_WIN_EVENT = 'pfe:cart-changed'

// Single long-lived BroadcastChannel reused across all emits, instead of
// opening/closing a fresh channel per message. Lazily created and feature-
// detected so it stays a no-op where BroadcastChannel is unavailable.
let sharedSyncChannel: any = null
let sharedSyncChannelInit = false

function getSyncChannel(): any {
    if (sharedSyncChannelInit) return sharedSyncChannel
    sharedSyncChannelInit = true
    try {
        // @ts-ignore
        if (typeof BroadcastChannel !== 'undefined') {
            // @ts-ignore
            sharedSyncChannel = new BroadcastChannel('pfe-cart-sync')
        }
    } catch {
        sharedSyncChannel = null
    }
    return sharedSyncChannel
}

function emitCartChanged(reason: string) {
    const payload = { ts: Date.now(), reason }
    try { localStorage.setItem(CART_SYNC_LS_KEY, JSON.stringify(payload)) } catch {}
    try { window.dispatchEvent(new CustomEvent(CART_SYNC_WIN_EVENT, { detail: payload })) } catch {}

    // BroadcastChannel is nicer than storage (no quota churn), but optional.
    try {
        const ch = getSyncChannel()
        if (ch) ch.postMessage(payload)
    } catch {}
}

export function onCartChanged(cb: (payload: any) => void) {
    const onStorage = (e: StorageEvent) => {
        if (e.key !== CART_SYNC_LS_KEY) return
        try { cb(JSON.parse(String(e.newValue || '{}'))) } catch { cb({ ts: Date.now(), reason: 'storage' }) }
    }
    const onWin = (e: any) => cb(e?.detail ?? { ts: Date.now(), reason: 'event' })

    window.addEventListener('storage', onStorage)
    window.addEventListener(CART_SYNC_WIN_EVENT, onWin as any)

    let ch: any = null
    try {
        // @ts-ignore
        if (typeof BroadcastChannel !== 'undefined') {
            // @ts-ignore
            ch = new BroadcastChannel('pfe-cart-sync')
            ch.onmessage = (msg: any) => cb(msg?.data ?? msg)
        }
    } catch {}

    return () => {
        window.removeEventListener('storage', onStorage)
        window.removeEventListener(CART_SYNC_WIN_EVENT, onWin as any)
        try { ch?.close?.() } catch {}
    }
}

// ---- API helpers --------------------------------------------------------
function apiBase() {
    const base = (getWidgetConfig().cartBackendBaseUrl as string) || ''
    return base.endsWith('/') ? base.slice(0, -1) : base
}

function apiUrl(path: string) {
    const base = apiBase()
    const p = path.startsWith('/') ? path : `/${path}`
    return `${base}${p}`
}

function getCartToken(): string {
    try { return localStorage.getItem(CART_TOKEN_KEY) || '' } catch { return '' }
}

function hasCartToken(): boolean {
    return !!String(getCartToken() || '').trim()
}

function setCartToken(token?: string) {
    if (!token) return
    try { localStorage.setItem(CART_TOKEN_KEY, token) } catch {}
}

function headersWithToken(extra?: Record<string, string>) {
    const t = getCartToken()
    return {
        ...(t ? { 'X-Cart-Token': t } : {}),
        ...(extra || {}),
    }
}

function isMatchingImageCartItem(item: any, imageId: string, projectCode?: string): boolean {
    const wantedId = String(imageId || '').trim()
    if (!wantedId) return false
    const itemId = String(item?.imageId ?? item?.preset?.imageId ?? item?.meta?.imageId ?? '').trim()
    if (itemId !== wantedId) return false
    if (!projectCode) return String(item?.kind || item?.type || '') === 'image'
    return String(item?.payload?.editorProjectId ?? '') === String(projectCode) && String(item?.kind || item?.type || '') === 'image'
}

async function getReadyLocalProjectImageRecords(projectCode: string): Promise<Array<ImageRecord | any>> {
    const code = String(projectCode || '').trim()
    if (!code) return []
    const db = useImageDB('imageDB')
    const records = await db.getByProjectCode(code).catch(() => [])
    return records.filter((rec: any) => !!imageItemFromRecord(rec, code))
}

export type CartItem = {
    id: string | number
    kind?: 'image' | 'product'
    productId?: number
    imageId?: string
    assetId?: string
    stockAssetId?: string
    preset?: any
    selectedVariantIds?: number[]
    dimensions?: { width: number; height: number }
    title?: string
    thumbUrl?: string
    meta?: string[]
    quantity: number
    unitPriceCents?: number
    lineTotalCents?: number
}

export type Cart = {
    id: string | number
    currency?: string
    items: CartItem[]
    displayGroups?: Array<{
        groupKey: string
        type: string
        typeLabel?: string
        title: string
        subtitle?: string
        meta?: string[]
        thumbUrl?: string | null
        totalQuantity?: number
        lineTotalCents?: number
        items?: Array<{
            id: string | number | null
            title: string
            quantity: number
            lineTotalCents: number
            thumbUrl?: string | null
            meta?: string[]
        }>
    }>
    totals?: { subtotalCents?: number; totalCents?: number; byType?: Record<string, number> }
    cartToken?: string
}

export type PendingMerge = {
    hasPending: boolean
    itemCount?: number
    uploadDate?: string | null
}

function toVariantNumber(v: any): number {
    if (v == null) return 0
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0
    const s = String(v).trim()
    if (!s) return 0
    const m = s.match(/(\d+)\s*$/)
    if (m) return Number(m[1]) || 0
    const n = Number(s)
    return Number.isFinite(n) ? n : 0
}

function normalizePresetForCart(rec: ImageRecord | any) {
    const preset = (rec?.edits?.preset || {}) as any
    const crop = rec?.edits?.crop
    const assetId = String(rec?.assetId || rec?.stockAssetId || preset?.assetId || preset?.stockAssetId || '').trim() || undefined
    const rotate = Number(rec?.edits?.rotate ?? preset?.rotate ?? 0) || 0
    const frozenOrientation = resolveFrozenOrientation(rec)
    const sourceWidth = Math.max(1, Math.round(Number(crop?.sourceWidth ?? rec?.editorWidth ?? rec?.width ?? 0) || 0))
    const sourceHeight = Math.max(1, Math.round(Number(crop?.sourceHeight ?? rec?.editorHeight ?? rec?.height ?? 0) || 0))
    const cropX = Math.max(0, Math.round(Number(crop?.x) || 0))
    const cropY = Math.max(0, Math.round(Number(crop?.y) || 0))
    const cropWidth = Math.max(1, Math.round(Number(crop?.w ?? crop?.width) || 0))
    const cropHeight = Math.max(1, Math.round(Number(crop?.h ?? crop?.height) || 0))
    const hasNormalizedCrop = crop && [crop?.nx, crop?.ny, crop?.nw, crop?.nh].every((value) => Number.isFinite(Number(value)))
    const nx = Math.max(0, Math.min(1, Number(hasNormalizedCrop ? crop?.nx : (cropX / sourceWidth)) || 0))
    const ny = Math.max(0, Math.min(1, Number(hasNormalizedCrop ? crop?.ny : (cropY / sourceHeight)) || 0))
    const nw = Math.max(0, Math.min(1, Number(hasNormalizedCrop ? crop?.nw : (cropWidth / sourceWidth)) || 0))
    const nh = Math.max(0, Math.min(1, Number(hasNormalizedCrop ? crop?.nh : (cropHeight / sourceHeight)) || 0))
    return {
        ...preset,
        ...frozenOrientation,
        ...(assetId ? { assetId, stockAssetId: assetId, stockId: assetId } : {}),
        ...(crop ? {
            crop: {
                x: cropX,
                y: cropY,
                w: cropWidth,
                h: cropHeight,
                width: cropWidth,
                height: cropHeight,
                nx,
                ny,
                nw,
                nh,
                ...(sourceWidth > 1 ? { sourceWidth } : {}),
                ...(sourceHeight > 1 ? { sourceHeight } : {}),
                ...(Number.isFinite(Number(crop?.rot)) ? { rot: Number(crop?.rot) || 0 } : {}),
            },
        } : {}),
        rotate,
    }
}

function selectedVariantIdsFromRecord(rec: ImageRecord | any): number[] {
    const preset = (rec?.edits?.preset || {}) as any
    return Array.from(new Set([
        toVariantNumber(preset?.size?.variantId || preset?.size?.id || 0),
        ...((Array.isArray(preset?.options) ? preset.options : []).map((o: any) => toVariantNumber(o?.variantId || 0))),
    ].filter((v) => v > 0)))
}

function hasCustomSizePreset(rec: ImageRecord | any): boolean {
    const preset = (rec?.edits?.preset || {}) as any
    const size = (preset?.size || {}) as any
    const widthMm = Math.round(Number(size?.widthMm || 0))
    const heightMm = Math.round(Number(size?.heightMm || 0))
    return widthMm > 0 && heightMm > 0
}

function hasRemoteUploadAsset(rec: ImageRecord | any): boolean {
    return !!String(rec?.upload?.remoteUrl || rec?.upload?.previewUrl || rec?.upload?.thumbUrl || '').trim()
}

function isUploadRecordReadyForCart(rec: ImageRecord | any): boolean {
    if (!hasRemoteUploadAsset(rec)) return false
    const state = String(rec?.upload?.state || '').trim()
    const phase = String(rec?.upload?.phase || '').trim()
    const editorReadyAt = Number(rec?.upload?.editorReadyAt || 0)
    if (state === 'done' || phase === 'done') return true
    return Number.isFinite(editorReadyAt) && editorReadyAt > 0
}

function imageItemFromRecord(rec: ImageRecord | any, projectCode: string): CartAddItem | null {
    const imageId = String(rec?.id || '').trim()
    const assetId = String(rec?.assetId || '').trim() || undefined
    const productId = Number(rec?.productId ?? rec?.edits?.preset?.productId ?? 0)
    const categoryId = Number(rec?.categoryId ?? rec?.edits?.preset?.categoryId ?? 0)
    if (!imageId || !(productId > 0) || !(categoryId > 0)) return null
    if (!isUploadRecordReadyForCart(rec)) return null

    const preset = normalizePresetForCart(rec)
    const quantity = Math.max(1, Number(preset?.quantity ?? 1))
    const selectedVariantIds = selectedVariantIdsFromRecord(rec)
    if (!selectedVariantIds.length && !hasCustomSizePreset(rec)) return null
    const rootCategoryId = Number(rec?.parentCategoryId ?? preset?.parentCategoryId ?? categoryId)
    const categoryTitle = String(rec?.categoryTitle ?? preset?.categoryTitle ?? '').trim() || undefined
    const rootCategoryTitle = String(rec?.parentCategoryTitle ?? preset?.parentCategoryTitle ?? categoryTitle ?? '').trim() || undefined
    const productTitle = String(rec?.productTitle ?? preset?.productTitle ?? '').trim() || undefined
    const meta = [
        productTitle ?? null,
        preset?.size?.title ?? null,
        ...(Array.isArray(preset?.options) ? preset.options.map((o: any) => o?.value ?? null) : []),
    ].filter(Boolean) as string[]

    return {
        kind: 'image',
        type: 'image',
        itemType: 'image',
        productId,
        imageId,
        assetId,
        uploadIds: [imageId],
        ...(assetId ? { stockAssetId: assetId } : {}),
        editorProjectId: projectCode,
        quantity,
        preset,
        categoryId,
        rootCategoryId,
        categoryTitle,
        rootCategoryTitle,
        productTitle,
        title: String(rec?.name || imageId),
        thumbUrl: String(rec?.upload?.thumbUrl || rec?.upload?.previewUrl || rec?.upload?.remoteUrl || '').trim() || undefined,
        meta,
        selectedVariantIds,
    }
}

function normalizeCompare(value: any): any {
    if (Array.isArray(value)) {
        return value.map(normalizeCompare)
    }
    if (value && typeof value === 'object') {
        const out: Record<string, any> = {}
        for (const key of Object.keys(value).sort()) out[key] = normalizeCompare(value[key])
        return out
    }
    return value
}

function sameImageItem(existing: any, desired: CartAddItem) {
    const existingPayload = {
        productId: Number(existing?.productId ?? 0),
        imageId: String(existing?.imageId ?? ''),
        assetId: String(existing?.payload?.assetId ?? ''),
        quantity: Math.max(1, Number(existing?.quantity ?? 1)),
        editorProjectId: String(existing?.payload?.editorProjectId ?? ''),
        categoryId: Number(existing?.payload?.categoryId ?? existing?.payload?.preset?.categoryId ?? 0),
        rootCategoryId: Number(existing?.payload?.rootCategoryId ?? existing?.payload?.parentCategoryId ?? existing?.payload?.preset?.parentCategoryId ?? 0),
        categoryTitle: String(existing?.payload?.categoryTitle ?? existing?.payload?.preset?.categoryTitle ?? ''),
        rootCategoryTitle: String(existing?.payload?.rootCategoryTitle ?? existing?.payload?.parentCategoryTitle ?? existing?.payload?.preset?.parentCategoryTitle ?? ''),
        preset: existing?.preset ?? null,
        selectedVariantIds: Array.isArray(existing?.selectedVariantIds) ? [...existing.selectedVariantIds].map((v: any) => Number(v)).sort((a: number, b: number) => a - b) : [],
    }
    const desiredPayload = {
        productId: Number(desired.productId ?? 0),
        imageId: String((desired as any).imageId ?? ''),
        assetId: String((desired as any).assetId ?? ''),
        quantity: Math.max(1, Number(desired.quantity ?? 1)),
        editorProjectId: String((desired as any).editorProjectId ?? ''),
        categoryId: Number((desired as any).categoryId ?? (desired as any)?.preset?.categoryId ?? 0),
        rootCategoryId: Number((desired as any).rootCategoryId ?? (desired as any)?.preset?.parentCategoryId ?? 0),
        categoryTitle: String((desired as any).categoryTitle ?? (desired as any)?.preset?.categoryTitle ?? ''),
        rootCategoryTitle: String((desired as any).rootCategoryTitle ?? (desired as any)?.preset?.parentCategoryTitle ?? ''),
        preset: (desired as any).preset ?? null,
        selectedVariantIds: Array.isArray((desired as any).selectedVariantIds) ? [...((desired as any).selectedVariantIds || [])].map((v: any) => Number(v)).sort((a: number, b: number) => a - b) : [],
    }
    return JSON.stringify(normalizeCompare(existingPayload)) === JSON.stringify(normalizeCompare(desiredPayload))
}

export type CartAddItem =
    | {
    kind: 'image'
    type?: 'image'
    itemType?: 'image'
    productId: number
    imageId: string
    assetId?: string
    uploadIds?: string[]
    editorProjectId?: string
    categoryId?: number
    rootCategoryId?: number
    categoryTitle?: string
    rootCategoryTitle?: string
    productTitle?: string
    quantity: number
    preset?: any
    title?: string
    thumbUrl?: string
    meta?: string[]
    selectedVariantIds?: number[]
}
    | {
    kind: 'product'
    type?: 'product'
    itemType?: 'product'
    productId: number
    quantity: number
    selectedVariantIds?: number[]
    dimensions?: { width: number; height: number }
    title?: string
    thumbUrl?: string
    meta?: string[]
}
    | {
    /** Design-Editor: Position referenziert ein Design-Dokument (Backend: CartService::addDesignRow) */
    kind: 'design'
    type?: 'design'
    itemType?: 'design'
    productId: number
    designDocumentId: string
    editorProjectId: string
    categoryId?: number
    rootCategoryId?: number
    categoryTitle?: string
    productTitle?: string
    quantity: number
    preset?: any
    selectedVariantIds?: number[]
    /** Druckfreigabe: Hash des freigegebenen Dokument-Stands (Server) + Zeitpunkt (ISO) */
    proofHash?: string | null
    proofConfirmedAt?: string | null
    title?: string
    thumbUrl?: string
    meta?: string[]
}

export const useCartStore = defineStore('cart', {
    state: () => ({
        cart: null as Cart | null,
        loading: false,
        error: null as string | null,
        cartLoadPromise: null as Promise<Cart | null> | null,
        cartRestorePromise: null as Promise<Cart | null> | null,
        pendingMerge: null as PendingMerge | null,
    }),

    getters: {
        items: (s) => s.cart?.items ?? [],
        subtotalCents: (s) => s.cart?.totals?.subtotalCents ?? 0,
        totalCents: (s) => s.cart?.totals?.totalCents ?? 0,
        count: (s) => (s.cart?.items ?? []).reduce((a, it) => a + (Number(it.quantity) || 0), 0),
        isEmpty: (s) => !(s.cart?.items?.length),
    },

    actions: {
        _applyCart(next: Cart) {
            this.cart = next
            if (next?.cartToken) {
                setCartToken(next.cartToken)
            } else if (next?.id != null && next.id !== '' && next.id !== 'local') {
                // A persisted cart WITHOUT a guest token belongs to a logged-in user:
                // the guest cart was merged on login. Drop the now-stale guest token so
                // a later editor session can't resurrect or duplicate the old guest cart.
                try { localStorage.removeItem(CART_TOKEN_KEY) } catch {}
            }
            return next
        },

        async restoreLocalProjectImages(projectCode?: string) {
            const code = String(projectCode || '').trim() || await useProjectStore().ensureProject().catch(() => '')
            if (!code) return this.cart
            if (this.cartRestorePromise) return this.cartRestorePromise

            const request = (async () => {
                if (!this.cart) {
                    await this.fetchCart().catch(() => null)
                }
                if (!this.cart) return this.cart
                const localRecords = await getReadyLocalProjectImageRecords(code)
                if (!localRecords.length) return this.cart

                const cartItems = this.cart?.items ?? []
                const missing = localRecords.filter((rec: any) => {
                    const imageId = String(rec?.id || '').trim()
                    if (!imageId) return false
                    return !cartItems.some((it: any) => isMatchingImageCartItem(it, imageId, code))
                })
                if (!missing.length) return this.cart

                return await this.reconcileProjectImages(missing, code)
            })()

            this.cartRestorePromise = request.finally(() => {
                if (this.cartRestorePromise === request) this.cartRestorePromise = null
            })
            return this.cartRestorePromise
        },

        async fetchCart() {
            if (this.cartLoadPromise) return this.cartLoadPromise

            this.loading = true
            this.error = null
            const request = (async () => {
                try {
                    const next = await $fetch<Cart>(apiUrl('/cart'), {
                        headers: headersWithToken(),
                    })
                    // IMPORTANT: NO emitCartChanged here (avoids infinite refresh loops)
                    return this._applyCart(next)
                } catch (e: any) {
                    this.error = e?.data?.message ?? e?.message ?? 'Cart konnte nicht geladen werden'
                    throw e
                } finally {
                    this.loading = false
                }
            })()

            this.cartLoadPromise = request.finally(() => {
                if (this.cartLoadPromise === request) this.cartLoadPromise = null
            })

            return this.cartLoadPromise
        },

        async addItems(payload: { items: CartAddItem[] }) {
            this.error = null
            try {
                const body: any = { items: payload.items }
                // Backend-Fallbacks: manche Endpunkte erwarten andere Feldnamen/Formate
                body.lineItems = payload.items
                if (payload.items.length === 1) {
                    const one: any = payload.items[0]
                    body.item = one
                    body.kind = one?.kind
                    body.type = one?.type || one?.kind
                    body.itemType = one?.itemType || one?.kind
                }

                const next = await $fetch<Cart>(apiUrl('/cart/items'), {
                    method: 'POST',
                    body,
                    headers: headersWithToken({ 'Content-Type': 'application/json' }),
                })
                const res = this._applyCart(next)
                emitCartChanged('addItems')
                return res
            } catch (e: any) {
                this.error = e?.data?.message ?? e?.message ?? 'Konnte nicht zum Cart hinzufügen'
                throw e
            }
        },

        async addItem(item: CartAddItem) {
            if (!item) return this.cart
            return this.addItems({ items: [item] })
        },

        async ensureCartLoaded() {
            if (typeof window === 'undefined') return this.cart
            if (this.cart) return this.cart
            return this.fetchCart()
        },

        hasStoredSession() {
            return hasCartToken() || !!this.cart?.cartToken || !!(this.cart?.items?.length)
        },

        async refreshCartIfSessionExists() {
            return this.fetchCart()
        },

        async primeCheckoutSession() {
            try {
                const next = await $fetch<Cart>(apiUrl('/cart'), {
                    headers: headersWithToken(),
                })
                return this._applyCart(next)
            } catch {
                return this.cart
            }
        },

        async addImagesToCart(input: {
            productId: number
            items: Array<{
                imageId: string
                assetId?: string
                uploadIds?: string[]
                quantity?: number
                preset?: any
                title?: string
                thumbUrl?: string
                meta?: string[]
                selectedVariantIds?: number[]
                categoryId?: number
                rootCategoryId?: number
                categoryTitle?: string
                rootCategoryTitle?: string
                productTitle?: string
                resolutionDpi?: number | null
            }>
        }) {
            const project = useProjectStore()
            const editorProjectId = await project.ensureProject().catch(() => '')
            const items: CartAddItem[] = (input.items || [])
                .filter((x) => !!x.imageId)
                .map((x) => ({
                    kind: 'image',
                    type: 'image',
                    itemType: 'image',
                    productId: input.productId,
                    imageId: x.imageId,
                    assetId: x.assetId,
                    uploadIds: Array.isArray(x.uploadIds) ? x.uploadIds.filter(Boolean) : (x.imageId ? [x.imageId] : undefined),
                    quantity: Math.max(1, Number(x.quantity ?? 1)),
                    preset: x.preset,
                    title: x.title,
                    thumbUrl: x.thumbUrl,
                    meta: x.meta,
                    selectedVariantIds: x.selectedVariantIds,
                    editorProjectId,
                    categoryId: Number(x.categoryId ?? 0) || undefined,
                    rootCategoryId: Number(x.rootCategoryId ?? 0) || undefined,
                    categoryTitle: String(x.categoryTitle ?? '').trim() || undefined,
                    rootCategoryTitle: String(x.rootCategoryTitle ?? '').trim() || undefined,
                    productTitle: String(x.productTitle ?? '').trim() || undefined,
                    resolutionDpi: x.resolutionDpi ?? undefined,
                }))

            if (!items.length) return this.cart
            return this.addItems({ items })
        },

        async addProductToCart(input: {
            productId: number
            quantity?: number
            selectedVariantIds?: number[]
            dimensions?: { width: number; height: number }
            title?: string
            thumbUrl?: string
            meta?: string[]
        }) {
            const item: CartAddItem = {
                kind: 'product',
                type: 'product',
                itemType: 'product',
                productId: input.productId,
                quantity: Math.max(1, Number(input.quantity ?? 1)),
                selectedVariantIds: input.selectedVariantIds,
                dimensions: input.dimensions,
                title: input.title,
                thumbUrl: input.thumbUrl,
                meta: input.meta,
            }
            return this.addItems({ items: [item] })
        },

        async removeItem(itemId: string | number) {
            this.error = null
            try {
                const next = await $fetch<Cart>(apiUrl(`/cart/items/${encodeURIComponent(String(itemId))}`), {
                    method: 'DELETE',
                    headers: headersWithToken(),
                })
                const res = this._applyCart(next)
                emitCartChanged('removeItem')
                return res
            } catch (e: any) {
                this.error = e?.data?.message ?? e?.message ?? 'Item konnte nicht entfernt werden'
                throw e
            }
        },

        /**
         * Falls das Bild bereits im Warenkorb ist: alten Eintrag entfernen
         * und mit dem neuen Preset / der neuen Produkt-ID neu hinzufügen.
         * Ist das Bild noch NICHT im Warenkorb, passiert nichts (kein Auto-Add).
         */
        async upsertImageInCart(input: {
            imageId: string
            productId: number
            quantity: number
            editorProjectId?: string
            preset?: any
            title?: string
            thumbUrl?: string
            meta?: string[]
            selectedVariantIds?: number[]
            assetId?: string
        }) {
            const wanted = String(input.imageId || '').trim()
            if (!wanted || !input.productId) return this.cart

            await this.ensureCartLoaded()

            const existing = (this.cart?.items ?? []).find((it: any) => {
                const id = String(it?.imageId ?? it?.preset?.imageId ?? it?.meta?.imageId ?? '').trim()
                return id === wanted
            }) as any

            // Bild noch nicht im Warenkorb → nichts tun
            if (!existing) return this.cart

            // Thumb + Titel aus altem Eintrag erhalten, falls nicht übergeben
            const thumbUrl = input.thumbUrl || existing.thumbUrl || undefined
            const title = input.title || existing.title || undefined
            try {
                const next = await $fetch<Cart>(apiUrl(`/cart/items/${encodeURIComponent(String(existing.id))}`), {
                    method: 'PATCH',
                    body: {
                        imageId: input.imageId,
                        assetId: input.assetId,
                        productId: input.productId,
                        quantity: Math.max(1, Number(input.quantity ?? 1)),
                        editorProjectId: input.editorProjectId,
                        preset: input.preset,
                        title,
                        thumbUrl,
                        metadata: input.meta,
                        selectedVariantIds: input.selectedVariantIds,
                    },
                    headers: headersWithToken(),
                })
                const res = this._applyCart(next)
                emitCartChanged('upsertImageInCart')
                return res
            } catch {
                return this.addImagesToCart({
                    productId: input.productId,
                    items: [{
                        imageId: input.imageId,
                        assetId: input.assetId,
                        quantity: input.quantity,
                        preset: input.preset,
                        title,
                        thumbUrl,
                        meta: input.meta,
                        selectedVariantIds: input.selectedVariantIds,
                    }],
                })
            }
        },

        async syncImageRecord(record: ImageRecord | any, projectCode?: string) {
            const code = String(projectCode || (record as any)?.projectCode || '').trim() || await useProjectStore().ensureProject().catch(() => '')
            if (!code) return this.cart

            await this.ensureCartLoaded()

            const desired = imageItemFromRecord(record, code)
            const existing = (this.cart?.items ?? []).find((it: any) => {
                const id = String(it?.imageId ?? '').trim()
                return id === String((record as any)?.id || '').trim()
                    && String(it?.payload?.editorProjectId ?? '') === code
                    && String(it?.kind || it?.type || '') === 'image'
            }) as any

            if (!desired) {
                if (existing) return this.removeItem(existing.id)
                return this.cart
            }

            if (existing && sameImageItem(existing, desired)) return this.cart

            if (existing) {
                const next = await $fetch<Cart>(apiUrl(`/cart/items/${encodeURIComponent(String(existing.id))}`), {
                    method: 'PATCH',
                    body: {
                        imageId: desired.imageId,
                        assetId: (desired as any).assetId,
                        uploadIds: (desired as any).uploadIds,
                        productId: desired.productId,
                        quantity: desired.quantity,
                        editorProjectId: desired.editorProjectId,
                        categoryId: (desired as any).categoryId,
                        rootCategoryId: (desired as any).rootCategoryId,
                        categoryTitle: (desired as any).categoryTitle,
                        rootCategoryTitle: (desired as any).rootCategoryTitle,
                        productTitle: (desired as any).productTitle,
                        preset: desired.preset,
                        title: desired.title,
                        thumbUrl: (desired as any).thumbUrl,
                        metadata: desired.meta,
                        selectedVariantIds: desired.selectedVariantIds,
                    },
                    headers: headersWithToken({ 'Content-Type': 'application/json' }),
                })
                const res = this._applyCart(next)
                emitCartChanged('syncImageRecord')
                return res
            }

            return this.addImagesToCart({
                productId: desired.productId,
                items: [{
                    imageId: desired.imageId,
                    assetId: (desired as any).assetId,
                    uploadIds: (desired as any).uploadIds,
                    quantity: desired.quantity,
                    preset: desired.preset,
                    title: desired.title,
                    thumbUrl: (desired as any).thumbUrl,
                    meta: desired.meta,
                    selectedVariantIds: desired.selectedVariantIds,
                    categoryId: (desired as any).categoryId,
                    rootCategoryId: (desired as any).rootCategoryId,
                    categoryTitle: (desired as any).categoryTitle,
                    rootCategoryTitle: (desired as any).rootCategoryTitle,
                    productTitle: (desired as any).productTitle,
                    resolutionDpi: (desired as any).resolutionDpi ?? (desired as any)?.preset?.resolutionDpi ?? null,
                }],
            })
        },

        async syncImageRecordIfInCart(record: ImageRecord | any, projectCode?: string) {
            const imageId = String((record as any)?.id || '').trim()
            if (!imageId) return this.cart
            const code = String(projectCode || (record as any)?.projectCode || '').trim() || await useProjectStore().ensureProject().catch(() => '')
            await this.ensureCartLoaded()
            const hasMatch = (this.cart?.items ?? []).some((it: any) => isMatchingImageCartItem(it, imageId, code || undefined))
            if (!hasMatch) return this.cart
            return this.syncImageRecord(record, code || undefined)
        },

        async hasImageInCart(imageId: string, projectCode?: string) {
            const wanted = String(imageId || '').trim()
            if (!wanted) return false
            const code = String(projectCode || '').trim()
            await this.ensureCartLoaded()
            return (this.cart?.items ?? []).some((it: any) => isMatchingImageCartItem(it, wanted, code || undefined))
        },

        async reconcileProjectImages(recordsOrIds: Array<ImageRecord | any | string>, projectCode?: string, opts?: { mode?: 'sync' | 'upsert' }) {
            const code = String(projectCode || '').trim() || await useProjectStore().ensureProject().catch(() => '')
            if (!code) return this.cart

            await this.ensureCartLoaded()

            const mode = opts?.mode === 'upsert' ? 'upsert' : 'sync'

            const items = (recordsOrIds || [])
                .map((entry: any) => typeof entry === 'string' ? null : imageItemFromRecord(entry, code))
                .filter(Boolean)

            const imageIds = Array.from(new Set((recordsOrIds || [])
                .map((entry: any) => typeof entry === 'string' ? String(entry || '').trim() : String(entry?.id || '').trim())
                .filter(Boolean)))

            const cartItems = this.cart?.items ?? []
            const filteredItems = mode === 'sync'
                ? items
                : items.filter((item: any) => {
                    const existing = cartItems.find((it: any) => isMatchingImageCartItem(it, String(item?.imageId || '').trim(), code))
                    if (!existing) return true
                    return !sameImageItem(existing, item)
                })
            const filteredImageIds = mode === 'sync'
                ? imageIds
                : imageIds.filter((imageId) => !cartItems.some((it: any) => isMatchingImageCartItem(it, imageId, code)))
            if (!filteredItems.length && !filteredImageIds.length) return this.cart

            this.error = null
            try {
                const body: Record<string, any> = {
                    projectCode: code,
                }
                body.mode = mode
                if (filteredItems.length) {
                    body.items = filteredItems
                } else if (filteredImageIds.length) {
                    body.imageIds = filteredImageIds
                }
                const next = await $fetch<Cart>(apiUrl('/cart/sync-images'), {
                    method: 'POST',
                    body,
                    headers: headersWithToken({ 'Content-Type': 'application/json' }),
                })
                const res = this._applyCart(next)
                emitCartChanged('reconcileProjectImages')
                return res
            } catch (e: any) {
                this.error = e?.data?.message ?? e?.message ?? 'Projektbilder konnten nicht synchronisiert werden'
                throw e
            }
        },

        async removeItemsByImageId(imageId: string) {
            const wanted = String(imageId || '').trim()
            if (!wanted) return this.cart

            await this.ensureCartLoaded()
            const matches = (this.cart?.items || []).filter((it: any) => {
                const id = String(it?.imageId ?? it?.preset?.imageId ?? it?.meta?.imageId ?? '').trim()
                return id === wanted
            })

            for (const item of matches) {
                await this.removeItem((item as any).id)
            }

            return this.cart
        },

        async setQuantity(itemId: string | number, quantity: number) {
            this.error = null
            const qty = Math.max(1, Number(quantity || 1))

            // optimistic — only the affected item's quantity is mutated here, so
            // we snapshot just that one field for rollback instead of deep-cloning
            // the entire cart. (On success _applyCart replaces the cart wholesale,
            // so totals are taken from the server response, not patched locally.)
            const it = this.cart?.items.find((x) => String(x.id) === String(itemId)) ?? null
            const prevQty = it ? it.quantity : null
            if (it) it.quantity = qty

            try {
                const next = await $fetch<Cart>(apiUrl(`/cart/items/${encodeURIComponent(String(itemId))}`), {
                    method: 'PATCH',
                    body: { quantity: qty },
                    headers: headersWithToken({ 'Content-Type': 'application/json' }),
                })
                const res = this._applyCart(next)
                emitCartChanged('setQuantity')
                return res
            } catch (e: any) {
                if (it && prevQty !== null) it.quantity = prevQty
                this.error = e?.data?.message ?? e?.message ?? 'Menge konnte nicht geändert werden'
                throw e
            }
        },

        async fetchPendingMerge(): Promise<PendingMerge> {
            try {
                const res = await $fetch<PendingMerge>(apiUrl('/cart/pending-merge'), {
                    headers: headersWithToken(),
                    credentials: 'include',
                })
                this.pendingMerge = res?.hasPending ? res : null
            } catch {
                this.pendingMerge = null
            }
            return this.pendingMerge ?? { hasPending: false }
        },

        async resolvePendingMerge(action: 'adopt' | 'discard'): Promise<Cart | null> {
            try {
                const next = await $fetch<Cart>(apiUrl('/cart/pending-merge'), {
                    method: 'POST',
                    body: { action },
                    headers: headersWithToken({ 'Content-Type': 'application/json' }),
                    credentials: 'include',
                })
                this.pendingMerge = null
                const res = this._applyCart(next)
                emitCartChanged('pendingMerge')
                return res
            } catch (e: any) {
                this.error = e?.data?.message ?? e?.message ?? 'Aktion fehlgeschlagen'
                throw e
            }
        },

        clearCartLocal(emit = true) {
            this.cart = this.cart
                ? { ...this.cart, items: [], totals: { subtotalCents: 0, totalCents: 0 } }
                : null
            if (emit) emitCartChanged('clearCartLocal')
        },

        clearToken(emit = true) {
            try { localStorage.removeItem(CART_TOKEN_KEY) } catch {}
            if (emit) emitCartChanged('clearToken')
        },
    },
})
